import { useNotification } from 'rc-notification';
import type { NotificationAPI, NotificationConfig } from 'rc-notification';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import './message.css?CSSModulesDisable';

import { CheckCircle, CloseCircle, InfoCircle, Loading } from '@webapp/common/assets/icons';

const prefixCls = 'tt-message';

let messageInstance: NotificationAPI | null = null;
let messageRoot: Root | null = null;
let incId = 0;

// v4 при maxCount: 1 не удалял старое уведомление, а переиспользовал его key —
// контент менялся в том же DOM-узле (без exit/enter). v5 просто обрезает список
// по maxCount, поэтому новое уведомление монтируется, а старое анимированно
// уезжает. Храним key текущего уведомления и для нового контента переиспользуем
// его — так возвращаем прежнее поведение «одно уведомление заменяется другим».
let activeNoticeKey: string | number | null = null;

// v5 отдаёт api только через hook, поэтому держим его в отдельном React-root
// и регистрируем здесь. Колбэки, пришедшие до монтирования, ждут в очереди.
const pendingCallbacks: Array<(instance: NotificationAPI) => void> = [];

const notificationConfig: NotificationConfig = {
    prefixCls,
    maxCount: 1,
    duration: 3,
    motion: { motionName: 'move-up' },
    style: () => ({ top: 0 }),
    getContainer: () => document.getElementById('notify') || document.body,
};

const MessageHolder: FC = () => {
    const [api, holder] = useNotification(notificationConfig);

    useEffect(() => {
        messageInstance = api;

        const callbacks = pendingCallbacks.splice(0);

        callbacks.forEach((callback) => callback(api));
    }, [api]);

    return holder;
};

const getMessageInstance = (callback: (instance: NotificationAPI) => void): void => {
    if (messageInstance) {
        return callback(messageInstance);
    }

    pendingCallbacks.push(callback);

    if (messageRoot) {
        return;
    }

    const container = document.createElement('div');

    document.body.appendChild(container);
    messageRoot = createRoot(container);
    messageRoot.render(<MessageHolder />);
};

enum AlertType {
    LOADING = 'loading',
    ERROR = 'error',
    INFO = 'info',
    SUCCESS = 'success',
}

const iconMap = {
    info: InfoCircle,
    success: CheckCircle,
    error: CloseCircle,
    loading: Loading,
};

const notice = (type: AlertType, msg: string, key?: string | number): void => {
    if (!msg) {
        return console.warn('empty alert');
    }

    if (typeof msg !== 'string') {
        msg = String(msg);
    }

    const IconComponent = iconMap[type];
    const logicalKey = key || ++incId;

    getMessageInstance((instance) => {
        const noticeKey = activeNoticeKey !== null ? activeNoticeKey : logicalKey;

        activeNoticeKey = noticeKey;

        instance.open({
            key: noticeKey,
            duration: 3,
            content: (
                <div className={`${prefixCls}-custom-content${type ? ` ${prefixCls}-${type}` : ''}`}>
                    {IconComponent && (
                        <span className={'tt-icon'}>
                            <IconComponent />
                        </span>
                    )}
                    <span>{msg}</span>.
                </div>
            ),
            onClose: () => {
                if (activeNoticeKey === noticeKey) {
                    activeNoticeKey = null;
                }
            },
        });
    });
};

type AlertCall = (msg?: string, key?: string | number) => void;

const info: AlertCall = (msg, key): void => notice(AlertType.INFO, msg, key);
const success: AlertCall = (msg = 'Сохранено', key): void => notice(AlertType.SUCCESS, msg, key);
const error: AlertCall = (msg = 'Ошибка', key): void => notice(AlertType.ERROR, msg, key);
const saving: AlertCall = (msg = 'Сохранение...', key): void => notice(AlertType.LOADING, msg, key);

const destroy = (): void => {
    activeNoticeKey = null;
    messageInstance?.destroy();
};

const Alert = { info, success, error, saving, destroy };

export { Alert };
